import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import SampleComment from '@components/SampleComment';
import { toastSuccess } from '@components/Toast';

import InnerTabsForNitexStudio from '@features/costing/components/InnerTabsForNitexStudio';
import InReviewBottomAction from '@features/costing/components/InReviewBottomAction';
import { generateCostingListParams } from '@features/filter/static/filterUtil';

import { clearCurrentActivePage, setCurrentActivePage } from '@store/actions/ActivePageAction';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { handleFilter } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { completeReview } from '@services/quoteServices';

import { changeDateFormat, printApiError } from '@utils/Util';

const StyleViewCostingItems = ({ sortValue }) => {
	const [selectedId, setSelectedId] = useState([]);
	const [selectedIds, setSelectedIds] = useState([]);
	const [selectAll, setSelectAll] = useState(false);
	const [isShowCosting, setIsShowCosting] = useState(false);
	const [showComment, setShowComment] = useState(false);
	const [selectedProductId, setSelectedProductId] = useState(null);

	const listOfCosting = useSelector((store) => store.filterResponseStore);
	const [updatedCostingList, setUpdatedCostingList] = useState([]);
	const activeTabInfo = useSelector((store) => store.costingStore.activeTabInfo);
	const activeTab = activeTabInfo?.activeTabName;
	const updatedCostingItemInfo = useSelector((store) => store.costingStore.updatedCostingItemInfo);
	const filterfields = useSelector((store) => store.filterFieldStore);
	const isAllSelected = listOfCosting?.data?.length === selectedIds?.length;
	const dispatch = useDispatch();

	useEffect(() => {
		if (listOfCosting.data) {
			setUpdatedCostingList(listOfCosting.data);
		}
	}, [listOfCosting.data]);

	const markAllToComplete = (mark) => {
		if (mark) {
			let ids = listOfCosting?.data?.map((i) => i.productId.toString()) || [];
			setSelectedIds(ids);
		} else {
			setSelectedIds([]);
		}
	};

	useEffect(() => {
		if (updatedCostingItemInfo && updatedCostingItemInfo.productId) {
			setUpdatedCostingList((prevList) => {
				let updatedList = prevList.map((item) =>
					item.productId === updatedCostingItemInfo.productId
						? {
								...item,
								basePrice: updatedCostingItemInfo?.totalCost,
								costingRemarks: updatedCostingItemInfo?.remarks
							}
						: item
				);

				if (activeTab === 'DESIGN_STUDIO_PENDING' && updatedCostingItemInfo?.isQuoted) {
					updatedList = updatedList.filter(
						(item) => item.productId !== updatedCostingItemInfo.productId
					);
				}

				if (activeTab === 'DESIGN_STUDIO_IN_REVIEW') {
					const now = new Date();
					const priceLastModifiedDate = new Date(updatedCostingItemInfo?.priceLastModifiedAt);
					const daysDifference = (now - priceLastModifiedDate) / (1000 * 60 * 60 * 24);

					if (daysDifference <= 90) {
						updatedList = updatedList.filter(
							(item) => item.productId !== updatedCostingItemInfo.productId
						);
					}
				}

				return updatedList;
			});
		}
	}, [updatedCostingItemInfo]);

	const onChangeCheckbox = (e, item) => {
		const { productId } = item;
		let temp = [...selectedIds];

		if (e.target.checked) {
			temp = [...temp, productId.toString()];
		} else {
			temp = temp.filter((id) => id !== productId.toString());
		}

		setSelectedIds(temp);
	};

	const onCompleteReview = async () => {
		try {
			dispatch(openLoader());
			const response = await completeReview(selectedIds);
			let requestBody = generateCostingListParams(filterfields, activeTabInfo.filterStatus);
			let queryString = `?page=0&size=20&sort=${sortValue}`;
			await dispatch(handleFilter(activeTabInfo.listApiUrl, queryString, false, 'POST', requestBody));
			toastSuccess(response?.message);

			setSelectedIds([]);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (selectedProductId !== null) {
			openDesignViewModal();
		}
	}, [selectedProductId]);

	const openDesignViewModal = () => {
		dispatch(
			setDesignViewModalInfo({
				id: selectedProductId,
				onCloseViewModal: () => {
					setSelectedProductId(null);
					setIsShowCosting(false);
					dispatch(clearDesignViewModalInfo({}));
				},
				source: 'ORDER_DETAILS',
				isShowCostingPanel: isShowCosting,
				isShowCostingButton: true
			})
		);
	};

	const handleCommentClose = () => {
		dispatch(clearCurrentActivePage());
		setShowComment(false);
	};

	const renderTableRow = (item, idx) => {
		return (
			<tr key={`${item.quantityWiseCostingId}_${idx}`}>
				{activeTabInfo?.activeTabName === 'DESIGN_STUDIO_IN_REVIEW' && (
					<td className="select-box-column">
						<div className="custom-chekbox d-inline-block align-middle">
							<div className="form-group m-0">
								<input
									type="checkbox"
									id={`costingSheet_${item?.productId}`}
									name={`costingSheet_${item?.productId}`}
									data-quantitywiseid={item?.productId}
									data-productid={item?.productId}
									onChange={(e) => onChangeCheckbox(e, item)}
									checked={selectedIds?.find(
										(value) => value?.toString() === item?.productId.toString()
									)}
								/>
								<label htmlFor={`costingSheet_${item?.productId}`} />
							</div>
						</div>
					</td>
				)}

				{/* design column */}
				<td
					className="cursor-pointer design-column"
					onClick={() => {
						setIsShowCosting(false);
						setSelectedProductId(item.productId);
					}}
				>
					<div className="d-flex align-items-center">
						<div className="design-image">
							<img src={item?.docUrl} alt="image" className="cell-img pr-2" />
						</div>
						<div className="ml-12">
							<p className="semibold-12 py-1">{item?.referenceNumber}</p>
							<ul className="common-list-component mb-2">
								{item?.productGroup && <li>{item?.productGroup}</li>}
								{item.subCategory && <li>{item?.subCategory} </li>}
							</ul>
							<ul className="common-list-component mb-1">
								{item?.ownerName && <li>{item?.ownerName}</li>}
							</ul>
						</div>
					</div>
				</td>

				<td>{item?.materialName}</td>

				<td
					className="production-price-column cursor-pointer"
					onClick={() => {
						setIsShowCosting(true);
						setSelectedProductId(item.productId);
					}}
				>
					<span className="regular-14 gray_dark_01">
						{item?.basePrice && `$${parseFloat(item?.basePrice).toFixed(2)}`}
					</span>
				</td>

				<td className="date-column">
					<p className="gray_dark_01 weight-500">
						{changeDateFormat(item?.basePriceUpdatedAt, '', 'Do MMM, YYYY')}
					</p>
				</td>

				{/* Remarks Column */}
				<td
					className="remarks-column cursor-pointer"
					onClick={() => {
						setIsShowCosting(true);
						setSelectedProductId(item.productId);
					}}
				>
					<p className="gray_dark_01">{item?.costingRemarks}</p>
				</td>
				{/* Conversation Column */}
				<td
					className={`${item.isSameProduct ? 'd-none' : ''} cursor-pointer`}
					rowSpan={item?.noOfSameProduct || 1}
				>
					<div className="conversation-btn">
						<button
							className="button text"
							onClick={() => {
								dispatch(
									setCurrentActivePage({
										id: item?.productId,
										type: 'PRODUCT_CONVERSATION',
										pageType: 'LIST'
									})
								);
								setSelectedId(item?.productId);
								setShowComment(true);
							}}
						>
							{item?.noOfUnreadMessage > 0 ? (
								<img
									src={`${process.env.PUBLIC_URL}/icons/comment-not-read.svg`}
									alt="not read"
								/>
							) : (
								<img
									src={`${process.env.PUBLIC_URL}/icons/comments-gray.svg`}
									alt="not read"
								/>
							)}
						</button>
					</div>
				</td>
			</tr>
		);
	};

	return (
		<>
			<div className="in-review-container">
				<div className="inner-tabs mb-3">
					<InnerTabsForNitexStudio />
				</div>
				<div className="costing-details-view-table scroll-x-label">
					<table className="table with-border">
						<thead>
							<tr>
								{activeTabInfo?.activeTabName === 'DESIGN_STUDIO_IN_REVIEW' && (
									<th className="select-box-column"></th>
								)}

								<th>Design</th>
								<th>Description</th>
								<th>Production price</th>

								<th>Last Modified Date</th>

								<th>Remarks</th>
								<th>Conversation</th>
							</tr>
						</thead>
						<tbody>{updatedCostingList?.map((item, idx) => renderTableRow(item, idx))}</tbody>
					</table>
					<div className="col-md-1 one-sixth text-right">
						{selectedId && (
							<SampleComment
								sampleRequestId={selectedId}
								postTypeFilter={'PRODUCT_DEVELOPMENT_COMMENT'}
								topic={'PRODUCT_QUOTE_COMMENT'}
								productId={selectedId}
								showComment={showComment}
								setShowComment={() => handleCommentClose()}
							/>
						)}
					</div>
				</div>
			</div>

			{/* Action Popup */}
			<InReviewBottomAction
				selectedIds={selectedIds}
				setSelectedIds={setSelectedIds}
				markAllToComplete={markAllToComplete}
				isAllSelected={isAllSelected}
				selectAll={selectAll}
				setSelectAll={setSelectAll}
				onCompleteReview={onCompleteReview}
				totalElements={listOfCosting?.totalElements}
				loadedItems={listOfCosting?.data?.length}
			/>
		</>
	);
};

export default StyleViewCostingItems;
